import { collectElementsDeepShadow } from './applyScopedControlbarHoverBackground';

/**
 * Extract `dyteThemeDataDetails` from axios success payloads (supports common nesting variants).
 * @param {{ data?: object } | null | undefined} axiosResponse
 */
export function getMeetingThemeDetails(axiosResponse) {
  const body = axiosResponse?.data;
  if (!body || typeof body !== 'object') {
    return null;
  }

  return (
    body.data?.dyteThemeDataDetails ??
    body.data?.data?.dyteThemeDataDetails ??
    body.dyteThemeDataDetails ??
    null
  );
}

/**
 * Outer `<rtk-meeting>` delegates to an inner `<rtk-meeting>`; `rtk-sidebar` lives in nested shadow trees.
 * @param {ShadowRoot | null} scopeRoot
 * @param {string} selector
 * @param {number} [maxDepth]
 * @returns {HTMLElement | null}
 */
function querySelectorInNestedShadowRoots(scopeRoot, selector, maxDepth = 14) {
  if (!scopeRoot || maxDepth <= 0) return null;
  const here = scopeRoot.querySelector(selector);
  if (here) return here;
  const nodes = scopeRoot.querySelectorAll('*');
  for (let i = 0; i < nodes.length; i++) {
    const el = nodes[i];
    if (el.shadowRoot) {
      const found = querySelectorInNestedShadowRoots(el.shadowRoot, selector, maxDepth - 1);
      if (found) return found;
    }
  }
  return null;
}

/**
 * Nested RealtimeKit hosts read --rtk-colors-* on their own :host; `provideRtkDesignSystem` should run on each.
 *
 * `rtk-participants` embeds its **own** `rtk-sidebar-ui` (tabs + `tab-participant-count-badge`); a shallow
 * `querySelector('rtk-sidebar-ui')` only hits the outer shell and misses badge/tab WCAG patches.
 *
 * @param {HTMLElement | null} meetingHostEl
 * @returns {HTMLElement[]}
 */
export function collectSidebarDesignSystemTargets(meetingHostEl) {
  if (!meetingHostEl?.shadowRoot) return [];
  const out = [];
  const sidebar =
    meetingHostEl.shadowRoot.querySelector('rtk-sidebar') ??
    querySelectorInNestedShadowRoots(meetingHostEl.shadowRoot, 'rtk-sidebar');
  if (sidebar) {
    out.push(sidebar);
    const sr = sidebar.shadowRoot;
    if (sr) {
      for (const ui of collectElementsDeepShadow(sr, 'rtk-sidebar-ui')) {
        out.push(ui);
      }
      for (const chat of collectElementsDeepShadow(sr, 'rtk-chat')) {
        out.push(chat);
      }
    }
  }
  return out;
}

/**
 * Pre-join setup UI (`rtk-setup-screen`) lives in nested shadow trees under `<rtk-meeting>`.
 * @param {HTMLElement | null} meetingHostEl
 * @returns {HTMLElement[]}
 */
export function collectSetupScreenDesignSystemTargets(meetingHostEl) {
  if (!meetingHostEl?.shadowRoot) return [];
  const setup =
    meetingHostEl.shadowRoot.querySelector('rtk-setup-screen') ??
    querySelectorInNestedShadowRoots(meetingHostEl.shadowRoot, 'rtk-setup-screen');
  if (!setup) return [];
  return [setup];
}

/**
 * Post-meeting UI (`rtk-ended-screen`) mounts when the call ends; it lives under nested `rtk-meeting`
 * shadows like setup. Message `<p>` uses `background-800` + `text-1000` (`rtk-ended-screen.css`).
 *
 * @param {HTMLElement | null} meetingHostEl
 * @returns {HTMLElement[]}
 */
export function collectEndedScreenDesignSystemTargets(meetingHostEl) {
  if (!meetingHostEl?.shadowRoot) return [];
  return collectElementsDeepShadow(meetingHostEl.shadowRoot, 'rtk-ended-screen');
}
